import { useState } from "react";
import axios from "axios";
import { Mail, MessageCircle, Send, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WHATSAPP_NUMBER = "18764104131";
const EMAIL = "rvvilla2020@gmail.com";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  check_in: "",
  check_out: "",
  guests: "",
  accommodation: "any",
  message: "",
};

const buildWhatsappUrl = (form, t) => {
  const lines = [
    `*${t.contact.title}*`,
    "",
    `${t.contact.form.name}: ${form.name}`,
    `${t.contact.form.email}: ${form.email}`,
    form.phone ? `${t.contact.form.phone}: ${form.phone}` : null,
    form.check_in ? `${t.contact.form.check_in}: ${form.check_in}` : null,
    form.check_out ? `${t.contact.form.check_out}: ${form.check_out}` : null,
    form.guests ? `${t.contact.form.guests}: ${form.guests}` : null,
    form.accommodation
      ? `${t.contact.form.accommodation}: ${t.contact.form.accommodation_options[form.accommodation]}`
      : null,
    form.message ? `\n${form.message}` : null,
  ].filter(Boolean);
  const text = encodeURIComponent(lines.join("\n"));
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
};

export const ContactSection = () => {
  const { t, lang } = useI18n();
  const f = t.contact.form;
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (key) => (e) =>
    setForm((s) => ({ ...s, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        check_in: form.check_in || null,
        check_out: form.check_out || null,
        guests: form.guests ? parseInt(form.guests, 10) : null,
        accommodation:
          form.accommodation === "any" ? null : form.accommodation,
        message: form.message.trim() || null,
        language: lang,
      };
      await axios.post(`${API}/bookings`, payload);
      toast.success(f.success);
      // open WhatsApp pre-filled
      window.open(buildWhatsappUrl(form, t), "_blank", "noopener,noreferrer");
      setForm(initialForm);
    } catch {
      toast.error(f.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 md:py-32 bg-[#F5F1E7]"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <span className="inline-block text-[#FFB800] font-bold tracking-[0.25em] text-xs md:text-sm uppercase">
              {t.contact.kicker}
            </span>
            <h2
              data-testid="contact-title"
              className="mt-4 text-[#1A1A1A] font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl"
              style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}
            >
              {t.contact.title}
            </h2>
            <p className="mt-5 text-[#4A4A4A] text-base md:text-lg leading-relaxed">
              {t.contact.lead}
            </p>

            <div className="mt-10 space-y-4">
              <a
                data-testid="whatsapp-link"
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 group rounded-2xl bg-white border border-black/5 p-5 hover:shadow-md transition-all"
              >
                <span className="w-12 h-12 rounded-full bg-[#2D5A27]/10 grid place-items-center text-[#2D5A27] group-hover:bg-[#2D5A27] group-hover:text-white transition-colors">
                  <MessageCircle className="w-5 h-5" />
                </span>
                <span>
                  <span className="block text-sm text-[#4A4A4A]">WhatsApp</span>
                  <span className="block font-semibold text-[#1A1A1A]">
                    +1 876 410 4131
                  </span>
                </span>
              </a>

              <a
                data-testid="email-link"
                href={`mailto:${EMAIL}`}
                className="flex items-center gap-4 group rounded-2xl bg-white border border-black/5 p-5 hover:shadow-md transition-all"
              >
                <span className="w-12 h-12 rounded-full bg-[#0D3B66]/10 grid place-items-center text-[#0D3B66] group-hover:bg-[#0D3B66] group-hover:text-white transition-colors">
                  <Mail className="w-5 h-5" />
                </span>
                <span>
                  <span className="block text-sm text-[#4A4A4A]">Email</span>
                  <span className="block font-semibold text-[#1A1A1A] break-all">
                    {EMAIL}
                  </span>
                </span>
              </a>

              <div className="flex items-center gap-4 rounded-2xl bg-white border border-black/5 p-5">
                <span className="w-12 h-12 rounded-full bg-[#FFB800]/15 grid place-items-center text-[#B27A00]">
                  <MapPin className="w-5 h-5" />
                </span>
                <span>
                  <span className="block text-sm text-[#4A4A4A]">
                    Negril, Jamaica
                  </span>
                  <span className="block font-semibold text-[#1A1A1A]">
                    Norman Manley Blv · Seven Miles Beach
                  </span>
                </span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              data-testid="booking-form"
              onSubmit={onSubmit}
              className="bg-white rounded-3xl border border-black/5 shadow-sm p-6 md:p-10"
            >
              <div data-testid="booking-form-note" className="mb-6 p-4 rounded-2xl bg-[#FFF7DB] border border-[#FFB800]/30 text-[#1A1A1A] text-sm leading-relaxed">
                <span className="font-semibold">📌</span> {t.contact.note}
              </div>
              <div className="grid md:grid-cols-2 gap-4 md:gap-5">
                <Field label={f.name} required>
                  <input
                    data-testid="booking-name"
                    required
                    value={form.name}
                    onChange={onChange("name")}
                    className="w-full rounded-xl bg-[#FAFAFA] border border-black/10 px-4 py-3 text-[#1A1A1A] focus:ring-2 focus:ring-[#FFB800] focus:border-transparent outline-none transition"
                  />
                </Field>
                <Field label={f.email} required>
                  <input
                    data-testid="booking-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={onChange("email")}
                    className="w-full rounded-xl bg-[#FAFAFA] border border-black/10 px-4 py-3 text-[#1A1A1A] focus:ring-2 focus:ring-[#FFB800] focus:border-transparent outline-none transition"
                  />
                </Field>
                <Field label={f.phone}>
                  <div className="relative">
                    <Phone className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#4A4A4A]" />
                    <input
                      data-testid="booking-phone"
                      value={form.phone}
                      onChange={onChange("phone")}
                      className="w-full rounded-xl bg-[#FAFAFA] border border-black/10 pl-10 pr-4 py-3 text-[#1A1A1A] focus:ring-2 focus:ring-[#FFB800] focus:border-transparent outline-none transition"
                    />
                  </div>
                </Field>
                <Field label={f.guests}>
                  <input
                    data-testid="booking-guests"
                    type="number"
                    min="1"
                    max="20"
                    value={form.guests}
                    onChange={onChange("guests")}
                    className="w-full rounded-xl bg-[#FAFAFA] border border-black/10 px-4 py-3 text-[#1A1A1A] focus:ring-2 focus:ring-[#FFB800] focus:border-transparent outline-none transition"
                  />
                </Field>
                <Field label={f.check_in}>
                  <input
                    data-testid="booking-checkin"
                    type="date"
                    value={form.check_in}
                    onChange={onChange("check_in")}
                    className="w-full rounded-xl bg-[#FAFAFA] border border-black/10 px-4 py-3 text-[#1A1A1A] focus:ring-2 focus:ring-[#FFB800] focus:border-transparent outline-none transition"
                  />
                </Field>
                <Field label={f.check_out}>
                  <input
                    data-testid="booking-checkout"
                    type="date"
                    value={form.check_out}
                    onChange={onChange("check_out")}
                    className="w-full rounded-xl bg-[#FAFAFA] border border-black/10 px-4 py-3 text-[#1A1A1A] focus:ring-2 focus:ring-[#FFB800] focus:border-transparent outline-none transition"
                  />
                </Field>
                <div className="md:col-span-2">
                  <Field label={f.accommodation}>
                    <select
                      data-testid="booking-accommodation"
                      value={form.accommodation}
                      onChange={onChange("accommodation")}
                      className="w-full rounded-xl bg-[#FAFAFA] border border-black/10 px-4 py-3 text-[#1A1A1A] focus:ring-2 focus:ring-[#FFB800] focus:border-transparent outline-none transition"
                    >
                      {Object.entries(f.accommodation_options).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <Field label={f.message}>
                    <textarea
                      data-testid="booking-message"
                      rows={4}
                      placeholder={f.message_ph}
                      value={form.message}
                      onChange={onChange("message")}
                      className="w-full rounded-xl bg-[#FAFAFA] border border-black/10 px-4 py-3 text-[#1A1A1A] focus:ring-2 focus:ring-[#FFB800] focus:border-transparent outline-none transition resize-none"
                    />
                  </Field>
                </div>
              </div>

              <button
                data-testid="booking-submit"
                type="submit"
                disabled={submitting}
                className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] hover:bg-[#2D5A27] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-full px-7 py-4 transition-all duration-300 hover:-translate-y-0.5"
              >
                <Send className="w-4 h-4" />
                {submitting ? f.submitting : f.submit}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-xs font-semibold tracking-wider uppercase text-[#4A4A4A] mb-2">
      {label} {required && <span className="text-[#FFB800]">*</span>}
    </span>
    {children}
  </label>
);

export default ContactSection;
